import useGiphyDialog from '@tenant/recognition/RecognitionModal/sendRecognitionView/giphySearch/useGiphyDialog'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

// NOTE: The `insertGif` Froala command itself is defined in froalaCustomCommands,
// which runs from the lazily-loaded editor chunk so `froala-editor` stays out of
// the initial bundle. This hook only wires the button's click callback to the
// GIF dialog for the active editor instance.

type UseFroalaGifsProps = {
  fieldName?: string
  modalId?: string
}

export const useFroalaGifs = ({
  fieldName = 'gif',
  modalId = 'gifModal',
}: UseFroalaGifsProps = {}) => {
  const giphyDialogId = useMemo(
    () => `rich-text-editor-${fieldName}-${modalId}`,
    [fieldName, modalId],
  )
  const { openGiphyDialog } = useGiphyDialog(giphyDialogId)
  const { setValue } = useFormContext()

  // Claims the GIF button's global click callback for this editor instance.
  const registerGifButtonCommand = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.__froalaGifButtonCallback = () => openGiphyDialog()
    }
  }, [openGiphyDialog])

  useEffect(() => {
    registerGifButtonCommand()

    return () => {
      if (typeof window !== 'undefined') {
        window.__froalaGifButtonCallback = undefined
      }
    }
  }, [registerGifButtonCommand])

  const handleGifSelected = (url: string) => {
    setValue(fieldName, url, { shouldValidate: true, shouldDirty: true })
  }

  return {
    giphyDialogId,
    handleGifSelected,
    registerGifButtonCommand,
  }
}

export default useFroalaGifs
